/**
 * Label printing strategy (production-first):
 *
 * 1) Zebra Browser Print (silent EPL) — optional, only if reachable on this PC
 * 2) Local CUPS agent (Ubuntu/dev only)
 * 3) System print dialog + Windows Zebra driver — works on all shop PCs with
 *    only the normal printer driver (no Browser Print required)
 *
 * Chrome often blocks http://LAN-IP → http://127.0.0.1 (Local Network Access),
 * so Browser Print may fail even when installed; dialog fallback covers that.
 */

export type LabelPrintChannel = 'browser-print' | 'local-agent' | 'system-dialog';

export interface LabelPrintSuccess {
  ok: true;
  channel: LabelPrintChannel;
}

export interface LabelPrintFailure {
  ok: false;
  message: string;
}

export type LabelPrintResult = LabelPrintSuccess | LabelPrintFailure;

function agentUrl(): string {
  const fromEnv = process.env.REACT_APP_LABEL_PRINT_AGENT_URL;
  return (
    fromEnv != null && fromEnv.trim() !== '' ? fromEnv.trim() : 'http://127.0.0.1:9102'
  ).replace(/\/$/, '');
}

function browserPrintBases(): string[] {
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return ['https://127.0.0.1:9101', 'http://127.0.0.1:9100'];
  }
  return ['http://127.0.0.1:9100', 'https://127.0.0.1:9101'];
}

interface BrowserPrintDevice {
  name?: string;
  uid?: string;
  connection?: string;
  deviceType?: string;
  version?: number;
  provider?: string;
  manufacturer?: string;
}

type FetchInit = RequestInit & { targetAddressSpace?: string };

async function fetchWithTimeout(
  url: string,
  init: FetchInit | undefined,
  ms: number,
): Promise<Response> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), ms);
  try {
    // targetAddressSpace hints Chrome Local Network Access (loopback Browser Print)
    return await fetch(url, {
      ...init,
      mode: 'cors',
      signal: controller.signal,
      targetAddressSpace: 'loopback',
    } as RequestInit);
  } finally {
    window.clearTimeout(timer);
  }
}

async function fetchJson<T>(url: string, ms: number): Promise<T> {
  const res = await fetchWithTimeout(url, undefined, ms);
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

function pickDevice(payload: unknown): BrowserPrintDevice | null {
  if (payload == null || typeof payload !== 'object') return null;
  const obj = payload as Record<string, unknown>;
  if (typeof obj.name === 'string' && obj.name.trim() !== '') {
    return obj as BrowserPrintDevice;
  }
  const printer = obj.printer;
  if (Array.isArray(printer) && printer.length > 0 && typeof printer[0] === 'object') {
    return printer[0] as BrowserPrintDevice;
  }
  if (Array.isArray(payload) && payload.length > 0 && typeof payload[0] === 'object') {
    return payload[0] as BrowserPrintDevice;
  }
  return null;
}

async function tryBrowserPrint(epl: string): Promise<LabelPrintResult | null> {
  for (const base of browserPrintBases()) {
    try {
      let device = pickDevice(await fetchJson<unknown>(`${base}/default`, 2500));
      if (device == null) {
        device = pickDevice(await fetchJson<unknown>(`${base}/available`, 2500));
      }
      if (device == null) continue;

      const res = await fetchWithTimeout(
        `${base}/write`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ device, data: epl }),
        },
        12000,
      );
      if (!res.ok) continue;
      return { ok: true, channel: 'browser-print' };
    } catch {
      // Browser Print missing, blocked by Chrome Local Network Access, etc.
    }
  }
  return null;
}

async function tryLocalAgent(epl: string): Promise<LabelPrintResult | null> {
  const url = `${agentUrl()}/print`;
  try {
    const res = await fetchWithTimeout(
      url,
      {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain; charset=utf-8' },
        body: epl,
      },
      15000,
    );
    const text = await res.text().catch(() => '');
    if (!res.ok) {
      return {
        ok: false,
        message:
          text.trim() !== ''
            ? text.trim()
            : `Agente local HTTP ${res.status}. ¿Corre npm run label-print-agent?`,
      };
    }
    return { ok: true, channel: 'local-agent' };
  } catch {
    return null;
  }
}

/**
 * Try silent channels only (no dialog).
 */
export async function printRawEplSilent(epl: string): Promise<LabelPrintResult> {
  if (epl.trim() === '') {
    return { ok: false, message: 'No hay datos EPL para imprimir.' };
  }

  const viaBrowser = await tryBrowserPrint(epl);
  if (viaBrowser != null) return viaBrowser;

  const viaAgent = await tryLocalAgent(epl);
  if (viaAgent != null) return viaAgent;

  return {
    ok: false,
    message: 'Silent print unavailable',
  };
}

/** @deprecated use printInventoryLabels which falls back to system dialog */
export async function printRawEpl(epl: string): Promise<LabelPrintResult> {
  return printRawEplSilent(epl);
}
